import { Form } from "antd";
import _ from "lodash";
import React, {
    forwardRef,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import message from "components/CustomMessage";
import { FieldTypes, generateFormFields } from "utils/formUtils";

import GenericMappingForm from "../GenericMappingForm";

const CONFIG_TYPES = {
    TARGETS: "Targets",
    TARGET_GROUP: "Target Groups",
};

const safeJsonParse = (value, fallback = []) => {
    if (!value) return fallback;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

const UltraVioLetMappingForm = forwardRef(
    function UltraVioLetMappingForm({ disableProjectFields = false, ...props }, ref) {
        const [form] = Form.useForm();

        const [targets] = useState(() => {
            if (props.record && props.record.type === CONFIG_TYPES.TARGETS && props.record.targetId) {
                const id = props.record.targetId;
                const name = props.record.targetName;

                const idArray = safeJsonParse(id, []);
                const nameArray = safeJsonParse(name, []);

                if (!Array.isArray(idArray)) {
                    return [{ id: idArray, name: nameArray || idArray }];
                }

                return idArray.map((targetId, idx) => ({
                    id: targetId,
                    name: nameArray[idx],
                }));
            }
            return [];
        });
        const targetsRef = useRef(targets);

        const [targetGroups] = useState(() => {
            if (props.record && props.record.type === CONFIG_TYPES.TARGET_GROUP && props.record.targetId) {
                const id = props.record.targetId;
                const name = props.record.targetName;

                const parsedId = safeJsonParse(id);
                const parsedName = safeJsonParse(name);

                const targetGroupId = Array.isArray(parsedId) ? parsedId[0] : parsedId;
                const targetGroupName = Array.isArray(parsedName) ? parsedName[0] : parsedName;

                return [{
                    id: targetGroupId,
                    name: targetGroupName,
                }];
            }
            return [];
        });
        const targetGroupsRef = useRef(targetGroups);

        const [initialValues] = useState(() => {
            if (props.record) {
                return [];
            }
            return [
                {
                    product: props.defaultProduct,
                    subProduct: props.defaultSubProduct,
                    subProductId: props.defaultSubProductId,
                    environment: props.defaultEnvironment,
                    configType: CONFIG_TYPES.TARGETS,
                },
            ];
        });

        const fetchTargets = useCallback(() => {
            const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(
                props.toolName,
                {
                    login_id: props.loginConfigId,
                    type: CONFIG_TYPES.TARGETS,
                }
            );

            const newPromise = promise
                .then((response) => {
                    const projects = response.projects || [];
                    targetsRef.current = _.uniqBy([...projects, ...targetsRef.current], "id");
                    return targetsRef.current;
                })
                .catch(() => {
                    message.error("Problem fetching targets");
                    return [];
                });

            return { promise: newPromise, cancel };
        }, [props.loginConfigId, props.toolName]);

        const fetchTargetGroups = useCallback(() => {
            const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(
                props.toolName,
                {
                    login_id: props.loginConfigId,
                    type: CONFIG_TYPES.TARGET_GROUP,
                }
            );
            const newPromise = promise
                .then((response) => {
                    const projects = response.projects || [];
                    targetGroupsRef.current = _.uniqBy([...projects, ...targetGroupsRef.current], "id");
                    return targetGroupsRef.current; 
                })
                .catch(() => {
                    message.error("Problem fetching target groups");
                    return [];
                });
            return { promise : newPromise, cancel };
        }, [props.loginConfigId, props.toolName]);

        const onConfigTypeChange = useCallback(() => {
            // Fields are rendered based on configType selection
            // API calls are handled by form framework via api prop
        }, []);

        const getExtraFieldsData = useCallback(
            (config) => {
                const { configType, targetIds, targetGroupId } = config;

                if (configType === CONFIG_TYPES.TARGET_GROUP) {
                    const targetGroup = targetGroupsRef.current.find((tg) => tg.id === targetGroupId);

                    return {
                        id: JSON.stringify([targetGroupId]),
                        name: JSON.stringify([targetGroup.name]),
                        type: CONFIG_TYPES.TARGET_GROUP,
                        ...(config.discovery_config_id && {
                            discovery_config_id: config.discovery_config_id,
                        }),
                    };
                } else {
                    const data = targetsRef.current.reduce(
                        (acc, cv) => {
                            const { id, name } = cv;
                            if (targetIds.includes(id)) {
                                acc.id.push(id);
                                acc.name.push(name);
                            }
                            return acc;
                        },
                        { id: [], name: [] },
                    );

                    return {
                        targetId: JSON.stringify(data.id),
                        targetName: JSON.stringify(data.name),
                        type: CONFIG_TYPES.TARGETS,
                        ...(config.discovery_config_id && {
                            discovery_config_id: config.discovery_config_id,
                        }),
                    };
                }
            },
            [],
        );

        const parseExtraFieldsDataFromResponse = useCallback((config) => {
            const { targetId, type } = config;
            const isTargetGroup = type === CONFIG_TYPES.TARGET_GROUP;
            const parsedIds = safeJsonParse(targetId, []);

            return {
                configType: isTargetGroup ? CONFIG_TYPES.TARGET_GROUP : CONFIG_TYPES.TARGETS,
                ...(isTargetGroup
                    ? { targetGroupId: Array.isArray(parsedIds) ? parsedIds[0] : parsedIds }
                    : { targetIds: Array.isArray(parsedIds) ? parsedIds : [parsedIds] }
                ),
                ...(config.discovery_config_id && {
                    discovery_config_id: config.discovery_config_id,
                }),
            };
        }, []);

        const projectListRender = useCallback(
            (listField) => {
                const selectedType = form.getFieldsValue().configurations[listField.name]?.configType;

                const targetFields = [
                    {
                        name: "targetIds",
                        colSpan: 12,
                        type: FieldTypes.MULTI_SELECT,
                        label: "Target",
                        disabled: disableProjectFields,
                        required: true,
                        props: {
                            api: fetchTargets,
                            allowClear: true,
                            placeholder: "Select Targets...",
                        },
                    },
                ];

                const targetGroupFields = [
                    {
                        name: "targetGroupId",
                        colSpan: 12,
                        type: FieldTypes.SELECT,
                        label: "Target Group",
                        disabled: disableProjectFields,
                        required: true,
                        props: {
                            api: fetchTargetGroups,
                            allowClear: true,
                            placeholder: "Select Target Group...",
                        },
                    },
                ];

                const formFields = selectedType === CONFIG_TYPES.TARGET_GROUP ? targetGroupFields : targetFields;

                return (
                    <div className="m-b-sm">
                        {generateFormFields(formFields, 12, listField)}
                    </div>
                );
            },
            [disableProjectFields, form, fetchTargets, fetchTargetGroups],
        );

        const extraFields = useMemo(
            () => [
                {
                    name: "configType",
                    type: FieldTypes.RADIO,
                    options: [
                        { label: "Map Targets", value: CONFIG_TYPES.TARGETS },
                        { label: "Map Target Groups", value: CONFIG_TYPES.TARGET_GROUP },
                    ],
                    disabled: disableProjectFields,
                    props: {
                        onChange: onConfigTypeChange,
                    },
                },
                {
                    name: (listField) => [listField.name, "projectList"],
                    render: projectListRender,
                },
            ],
            [projectListRender, disableProjectFields, onConfigTypeChange],
        );

        return (
            <GenericMappingForm
                ref={ref}
                form={form}
                title="Map Ultraviolet Targets"
                getExtraFieldsData={getExtraFieldsData}
                parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
                extraFields={extraFields}
                formDefaultValues={initialValues}
                newRowDefaultValue={initialValues[0]}
                {...props}
            />
        );
    },
);

export default UltraVioLetMappingForm;
